import { useState, useEffect } from 'react';

// Story 2.6: Mobile-first responsive breakpoint detection
export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface BreakpointState {
  current: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLargeDesktop: boolean;
}

const breakpoints = {
  sm: 320,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1400,
} as const;

/**
 * Hook for responsive breakpoint detection
 * Provides current breakpoint and device type helpers
 */
export const useBreakpoint = (): BreakpointState => {
  const [current, setCurrent] = useState<Breakpoint>(() => {
    // Initialize with actual window width to prevent flash
    if (typeof window === 'undefined') return 'sm';
    
    const width = window.innerWidth;
    if (width >= breakpoints['2xl']) return '2xl';
    if (width >= breakpoints.xl) return 'xl';
    if (width >= breakpoints.lg) return 'lg';
    if (width >= breakpoints.md) return 'md';
    return 'sm';
  });

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;

      if (width >= breakpoints['2xl']) {
        setCurrent('2xl');
      } else if (width >= breakpoints.xl) {
        setCurrent('xl');
      } else if (width >= breakpoints.lg) {
        setCurrent('lg');
      } else if (width >= breakpoints.md) {
        setCurrent('md');
      } else {
        setCurrent('sm');
      }
    };

    // Listen for resize events
    window.addEventListener('resize', updateBreakpoint);

    return () => {
      window.removeEventListener('resize', updateBreakpoint);
    };
  }, []);

  return {
    current,
    isMobile: current === 'sm',
    isTablet: current === 'md',
    isDesktop: current === 'lg' || current === 'xl',
    isLargeDesktop: current === '2xl',
  };
};
